import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, Search, Copy, FileDown, ChevronLeft, ChevronRight, FileText, Pencil, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import Swal from 'sweetalert2'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { DiscountListRow } from '@/services/discountService'
import { discountService } from '@/services/discountService'
import { useAuthStore } from '@/store/auth.store'

const PAGE_SIZE_OPTIONS = [10, 25, 50] as const

function formatListDate(iso: string, locale: string) {
  if (!iso) return '—'
  const d = new Date(`${iso}T12:00:00`)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(locale === 'ar' ? 'ar-SA' : 'en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

export const InventoryDiscountsList: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const selectedBranchId = Number(user?.selectedBranch?.branchId ?? 0)

  const [rows, setRows] = useState<DiscountListRow[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState<number>(10)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    if (!selectedBranchId) {
      setRows([])
      setTotalCount(0)
      return
    }
    let cancelled = false
    setLoading(true)
    void discountService
      .getAll(selectedBranchId, pageNumber, pageSize)
      .then(({ items, totalCount: tc }) => {
        if (!cancelled) {
          setRows(items)
          setTotalCount(tc)
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setRows([])
          setTotalCount(0)
          const msg =
            e && typeof e === 'object' && 'response' in e
              ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
              : undefined
          toast.error(msg || t('inventoryDiscounts.listLoadFailed'))
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedBranchId, pageNumber, pageSize, refreshKey, t])

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) =>
      [r.code, r.date, r.startDate, r.endDate].some((field) =>
        String(field).toLowerCase().includes(q),
      ),
    )
  }, [rows, searchQuery])

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  const page = Math.min(pageNumber, totalPages)

  const startIdx = totalCount === 0 ? 0 : (page - 1) * pageSize + 1
  const endIdx = Math.min(page * pageSize, totalCount)

  const handleAdd = () => navigate('/inventory/schemes-discounts/discounts/new')

  const handleEdit = (row: DiscountListRow) => {
    navigate(`/inventory/schemes-discounts/discounts/${row.id}/edit`)
  }

  const handleDelete = (row: DiscountListRow) => {
    if (!selectedBranchId || row.serverDiscountId <= 0) {
      toast.error(t('inventoryDiscounts.deleteFailed'))
      return
    }
    void (async () => {
      const result = await Swal.fire({
        icon: 'warning',
        title: t('inventoryDiscounts.deleteConfirmTitle'),
        text: t('inventoryDiscounts.deleteConfirmText', { code: row.code }),
        showCancelButton: true,
        confirmButtonText: t('inventoryDiscounts.deleteConfirmAction'),
        cancelButtonText: t('common.cancel'),
      })
      if (!result.isConfirmed) return
      setDeletingId(row.id)
      try {
        await discountService.delete(selectedBranchId, row.serverDiscountId)
        toast.success(t('inventoryDiscounts.deleted'))
        setRefreshKey((k) => k + 1)
      } catch (e: unknown) {
        const msg =
          e && typeof e === 'object' && 'response' in e
            ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
            : undefined
        toast.error(msg || t('inventoryDiscounts.deleteFailed'))
      } finally {
        setDeletingId(null)
      }
    })()
  }

  const handleCopy = () => {
    if (!selectedBranchId || filtered.length === 0) {
      toast.error(t('inventoryDiscounts.nothingToCopy'))
      return
    }
    const header = [
      t('inventoryDiscounts.columns.code'),
      t('inventoryDiscounts.columns.date'),
      t('inventoryDiscounts.columns.startDate'),
      t('inventoryDiscounts.columns.endDate'),
    ]
    const lines = filtered.map((r) =>
      [
        r.code,
        formatListDate(r.date, i18n.language),
        formatListDate(r.startDate, i18n.language),
        formatListDate(r.endDate, i18n.language),
      ].join('\t'),
    )
    void navigator.clipboard.writeText([header.join('\t'), ...lines].join('\n'))
    toast.success(t('inventoryDiscounts.copied'))
  }

  const handleExportCSV = () => {
    if (!selectedBranchId || filtered.length === 0) {
      toast.error(t('inventoryDiscounts.nothingToExport'))
      return
    }
    const header = 'Code,Date,Start Date,End Date'
    const body = filtered
      .map((r) =>
        [
          r.code,
          formatListDate(r.date, i18n.language),
          formatListDate(r.startDate, i18n.language),
          formatListDate(r.endDate, i18n.language),
        ]
          .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
          .join(','),
      )
      .join('\n')
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `discounts_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success(t('inventoryDiscounts.exported'))
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('inventoryDiscounts.title')}</h1>
            <p className="text-muted-foreground text-sm">{t('inventoryDiscounts.listSubtitle')}</p>
          </div>
        </div>
        <Button onClick={handleAdd} className="w-full sm:w-auto shrink-0">
          <Plus className="mr-2 h-4 w-4" />
          {t('inventoryDiscounts.addDiscount')}
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
            <div className="flex-1">
              <label className="mb-2 block text-sm font-medium">{t('inventoryDiscounts.search')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t('inventoryDiscounts.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                  }}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground whitespace-nowrap">
                  {t('inventoryDiscounts.showEntries')}
                </span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(v) => {
                    setPageSize(Number(v))
                    setPageNumber(1)
                  }}
                >
                  <SelectTrigger className="h-9 w-[72px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <FileDown className="mr-2 h-4 w-4" />
                CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="mr-2 h-4 w-4" />
                {t('inventoryDiscounts.copy')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                  <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                    {t('inventoryDiscounts.columns.code')}
                  </TableHead>
                  <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                    {t('inventoryDiscounts.columns.date')}
                  </TableHead>
                  <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                    {t('inventoryDiscounts.columns.startDate')}
                  </TableHead>
                  <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                    {t('inventoryDiscounts.columns.endDate')}
                  </TableHead>
                  <TableHead className="h-12 w-[72px] bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                    {t('inventoryDiscounts.columns.edit')}
                  </TableHead>
                  <TableHead className="h-12 w-[72px] bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                    {t('inventoryDiscounts.columns.delete')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!selectedBranchId ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                      {t('unsellableInventory.selectBranchForWarehouses')}
                    </TableCell>
                  </TableRow>
                ) : loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                      <div className="p-12 text-center">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                        <p className="mt-4 text-muted-foreground">{t('common.loading')}</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                      {t('inventoryDiscounts.emptyTable')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((row) => (
                    <TableRow
                      key={row.id}
                      className="border-b transition-colors hover:bg-primary/5 dark:hover:bg-primary/10"
                    >
                      <TableCell className="px-4 py-2 text-center text-sm">{row.code}</TableCell>
                      <TableCell className="px-4 py-2 text-center text-sm">
                        {formatListDate(row.date, i18n.language)}
                      </TableCell>
                      <TableCell className="px-4 py-2 text-center text-sm">
                        {formatListDate(row.startDate, i18n.language)}
                      </TableCell>
                      <TableCell className="px-4 py-2 text-center text-sm">
                        {formatListDate(row.endDate, i18n.language)}
                      </TableCell>
                      <TableCell className="px-4 py-2 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => handleEdit(row)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TableCell>
                      <TableCell className="px-4 py-2 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          disabled={deletingId === row.id || row.serverDiscountId <= 0}
                          onClick={() => handleDelete(row)}
                          aria-label={t('inventoryDiscounts.columns.delete')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {selectedBranchId ? (
            <div className="flex flex-col gap-3 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                {t('inventoryDiscounts.showing', { start: startIdx, end: endIdx, total: totalCount })}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  )
}
